/**
 * The helpdesk's motion, in one place so that everything moves alike and inside the
 * brand's limits: nothing a reader waits on runs past 400ms, and nothing bounces.
 * HelpdeskApp's MotionConfig reduces it to plain fades for someone who asked for less
 * motion. It does not reach a height animation, which is why `fold` takes the setting.
 */

import type { Transition, Variants } from "motion/react"

/** The brand's easing for interface movement, and the softer one for reveals. */
const EASE_UI = [0.22, 1, 0.36, 1] as const
const EASE_REVEAL = [0.16, 1, 0.3, 1] as const

/** Something travelling between two places, such as the current-page marker. */
export const TRAVEL: Transition = { duration: 0.28, ease: EASE_UI }

/** A swap in place, quick enough that nobody waits for it. */
export const SWAP: Transition = { duration: 0.15, ease: "easeOut" }

/** A group whose children arrive one after the other. */
export const REVEAL_GROUP: Variants = {
  hidden: {},
  shown: { transition: { staggerChildren: 0.05 } },
}

/** One child of a REVEAL_GROUP. */
export const REVEAL_ITEM: Variants = {
  hidden: { opacity: 0, y: 12 },
  shown: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: EASE_REVEAL },
  },
}

/** A whole page arriving. */
export const PAGE_ENTER = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.25, ease: EASE_REVEAL },
} as const

/**
 * Content folding open and shut. The clip lifts once it is open, or it would cut
 * off what travels through it, such as the current-page marker. With less motion
 * the height changes at once, so the rows around it stay put, and only the fade runs.
 */
export function fold(reducedMotion: boolean | null) {
  return {
    initial: { height: 0, opacity: 0, overflow: "hidden" },
    animate: {
      height: "auto",
      opacity: 1,
      transitionEnd: { overflow: "visible" },
    },
    exit: { height: 0, opacity: 0, overflow: "hidden" },
    transition: reducedMotion ? { ...TRAVEL, height: { duration: 0 } } : TRAVEL,
  } as const
}

/**
 * The line between two step numbers drawing itself, one step after the other. The
 * delay stops growing, so a long list's later lines do not lag behind the scroll.
 */
export function railDraw(index: number): Transition {
  return { duration: 0.4, ease: EASE_REVEAL, delay: Math.min(index, 4) * 0.08 }
}
